import { useEffect, useRef, useState } from 'react'
import { ClipboardPaste, Copy } from 'lucide-react'
import { AdvancedDisclosure, Alert, Button, Dialog, Field, Input, Textarea } from '@/components/common'
import { NATIVE_CLIPBOARD_LIMIT, readNativeClipboard, nativePasteIssue } from './nativeClipboard'
import { componentInstanceName } from './nativeSportsbookManifest'
import { isLayoutClipboard } from './nativeLayoutClipboard'
import { gamePageLabel } from './gamePageModel'
import { experienceTargetOptions } from './experienceModel'
import { TargetPicker } from './experienceEditorControls'
import styles from './nativeClipboard.module.css'

const textEditing = (target) => target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])')

function PasteDialog({ workspace, organization, experience, catalog, onClose, text, setText, readState, readIssue, readClipboard }) {
  const [error, setError] = useState('')
  const [kind, setKind] = useState('league')
  const [value, setValue] = useState('')
  const [name, setName] = useState('')
  const document = experience ? { ...workspace.draft, experience: workspace.draft.experience ?? experience } : workspace.draft
  const suggestedName = experienceTargetOptions(catalog ?? { events: [] }, kind).find((item) => item.value === value)?.label
  const pasteDestination = { experience, scope: { kind, value }, name: name.trim() || suggestedName }
  let payload = null, validationIssue = ''
  if (text.trim()) {
    try {
      payload = readNativeClipboard(text, organization, workspace.registry)
      validationIssue = nativePasteIssue(document, workspace.editorSelection, payload, workspace.registry, pasteDestination)
    }
    catch (failure) { validationIssue = failure.message }
  }
  const insert = () => {
    try { if (isLayoutClipboard(payload.kind)) workspace.pasteContent(text, pasteDestination); else workspace.pasteContent(text); onClose() }
    catch (failure) { setError(failure.message) }
  }
  const sourceName = payload && (payload.kind === 'event_layout' ? gamePageLabel(payload.content) : isLayoutClipboard(payload.kind) ? payload.content.name : payload.kind === 'section' ? componentInstanceName(payload.content) : payload.content.headline || 'Featured card')
  const destination = workspace.selectedComponent ? componentInstanceName(workspace.selectedComponent) : 'Home'
  const requiredDestination = workspace.registry?.components.find((item) => item.type === workspace.selectedComponent?.type)?.policy.required || workspace.selectedComponent?.type === 'compliance_notice'
  return <Dialog open title="Paste content" onClose={onClose} size="default" footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!payload || Boolean(validationIssue) || readState === 'loading'} onClick={insert}>Insert copy</Button></>}>
    <div className={styles.dialogContent}>
      {payload?.kind === 'event_layout' ? <><p>Copy {sourceName} to another sport, league or game.</p>
        <TargetPicker catalog={catalog ?? { events: [] }} kind={kind} value={value} onKindChange={setKind} onChange={setValue} disabled={false} />
        <Field label="Name"><Input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} placeholder={suggestedName || sourceName} /></Field>
      </> : payload?.kind === 'home_variation' ? <p>Add this variation {workspace.editorSelection.sceneId ? 'below the selected variation' : 'at the end of the list'}. Earlier matching variations take priority. Its order and visibility use existing Home content.</p>
        : payload ? <p>{payload.kind === 'card' ? `Add a card to ${destination}.` : `Add a section ${requiredDestination ? 'before' : 'after'} ${destination}.`} The new copy can be edited independently.</p>
          : <p>Paste a section, card, game page or Home variation copied from App Builder.</p>}
      {readIssue ? <Alert variant="warning">{readIssue}</Alert> : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      {validationIssue ? <Alert variant="error">{validationIssue}</Alert> : null}
      {payload ? <p className={styles.preview}><strong>{sourceName}</strong><span>{{ card: 'Featured card', section: 'Home section', event_layout: 'Game page', home_variation: 'Home variation' }[payload.kind]}</span></p> : null}
      <Button variant="ghost" size="compact" loading={readState === 'loading'} onClick={readClipboard}>{text ? 'Refresh clipboard' : 'Read clipboard'}</Button>
      <AdvancedDisclosure key={readState} label="Paste content manually" defaultOpen={readState === 'unavailable' || (readState === 'ready' && !text)}><Field label="Copied content"><Textarea value={text} onChange={(event) => { setText(event.target.value); setError('') }} rows={5} maxLength={NATIVE_CLIPBOARD_LIMIT} placeholder="Paste the content you copied from App Builder" /></Field></AdvancedDisclosure>
    </div>
  </Dialog>
}

export function NativeClipboardControls({ workspace, organization, disabled, experience, catalog }) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [fallback, setFallback] = useState('')
  const [text, setText] = useState('')
  const [readState, setReadState] = useState('idle')
  const [readIssue, setReadIssue] = useState('')
  const pasteRequest = useRef(0)
  useEffect(() => () => { pasteRequest.current += 1 }, [])
  const close = () => { pasteRequest.current += 1; setOpen(false) }
  const openPaste = async () => {
    const request = ++pasteRequest.current
    setOpen(true); setText(''); setReadIssue(''); setReadState('loading'); setMessage('')
    try {
      const value = await navigator.clipboard.readText()
      if (request === pasteRequest.current) { setText(value); setReadState('ready') }
    } catch {
      if (request === pasteRequest.current) { setReadState('unavailable'); setReadIssue('Clipboard access is unavailable. Paste manually using your keyboard or device paste menu.') }
    }
  }
  const latest = useRef({ workspace, disabled, experience, organization })
  useEffect(() => { latest.current = { workspace, disabled, experience, organization } })
  const copy = async () => {
    try {
      const content = latest.current.experience ? latest.current.workspace.copyContent(latest.current.experience) : latest.current.workspace.copyContent()
      setMessage(''); setFallback('')
      try { await navigator.clipboard.writeText(content.text); setMessage(`${content.name} copied.`) }
      catch { setFallback(content.text); setMessage('Clipboard access is unavailable. Copy the content below using your keyboard or device selection menu.') }
    } catch (error) { setMessage(error.message) }
  }
  useEffect(() => {
    const onCopy = (event) => {
      if (latest.current.disabled || event.defaultPrevented || textEditing(event.target) || event.target?.closest?.('[role="dialog"]') || window.getSelection()?.toString()) return
      try {
        const content = latest.current.experience ? latest.current.workspace.copyContent(latest.current.experience) : latest.current.workspace.copyContent()
        event.clipboardData.setData('text/plain', content.text)
        event.preventDefault(); setMessage(`${content.name} copied.`)
      } catch (error) { setMessage(error.message) }
    }
    const onPaste = (event) => {
      if (latest.current.disabled || event.defaultPrevented || textEditing(event.target) || event.target?.closest?.('[role="dialog"]')) return
      event.preventDefault()
      try {
        const copiedText = event.clipboardData.getData('text/plain')
        const payload = readNativeClipboard(copiedText, latest.current.organization, latest.current.workspace.registry)
        if (isLayoutClipboard(payload.kind)) { pasteRequest.current += 1; setText(copiedText); setReadState('ready'); setReadIssue(''); setOpen(true) }
        else latest.current.workspace.pasteContent(copiedText)
        setMessage(''); setFallback('')
      }
      catch (error) { setMessage(error.message) }
    }
    window.addEventListener('copy', onCopy); window.addEventListener('paste', onPaste)
    return () => { window.removeEventListener('copy', onCopy); window.removeEventListener('paste', onPaste) }
  }, [])
  const surface = workspace.editorSelection.surface
  const cannotCopy = surface === 'events' ? !experience : surface === 'scenes' ? !experience?.home_scenes.some((item) => item.id === workspace.editorSelection.sceneId) : !workspace.selectedComponent || workspace.selectedComponent.type === 'compliance_notice'
  const copyLabel = surface === 'events' ? 'page' : surface === 'scenes' ? 'variation' : workspace.selectedCard ? 'card' : 'section'
  return <>
    <div role="group" aria-label="Copy and paste content" className={styles.buttons}>
      <Button size="compact" variant="ghost" disabled={disabled || cannotCopy} onClick={copy}><Copy size={14} aria-hidden="true" />Copy {copyLabel}</Button>
      <Button size="compact" variant="ghost" disabled={disabled} onClick={openPaste}><ClipboardPaste size={14} aria-hidden="true" />Paste</Button>
    </div>
    {message ? <p role="status" className={styles.message}>{message}</p> : null}
    {fallback ? <AdvancedDisclosure label="Copy content manually"><Field label="Content to copy"><Textarea readOnly value={fallback} onFocus={(event) => event.target.select()} rows={4} /></Field></AdvancedDisclosure> : null}
    {open && !disabled ? <PasteDialog {...{ workspace, organization, experience, catalog }} onClose={close} text={text} setText={setText} readState={readState} readIssue={readIssue} readClipboard={openPaste} /> : null}
  </>
}
